import { forwardRef, useState } from 'react'

import { Group } from 'globalTypes'
import Autosuggest from 'react-autosuggest'
import TagsInput from 'react-tagsinput'

import { gql, useMutation } from '@apollo/client'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Button from 'Root/App/Form/Button'

import autosuggest from './AddUser/autosuggest.module.css'
import styles from './add-form.module.css'
import GroupTag from './group-tag'

const ADD_AUTHORIZATION_TO_GROUPS = gql`
  mutation AddAuthorizationsGroups($elementId: ID!, $groupsIds: [ID!]!) {
    addAuthorizationsGroups(elementId: $elementId, groupsIds: $groupsIds)
  }
`

const GroupSuggestion = ({ name }: { name: string }) => (
  <span className="flex flex-row gap-1 p-1">
    <div className="flex flex-col flex-grow my-auto text-gray-600 text-sm">
      <div className="font-medium">{name}</div>
    </div>
  </span>
)

interface GroupAutosuggestProps {
  workspaceGroups: Group[]
  ref?: React.Ref<HTMLInputElement>
  [props: string]: any
}

const GroupAutosuggest = forwardRef<HTMLInputElement, GroupAutosuggestProps>(
  ({ addTag, workspaceGroups, ...props }, ref) => {
    const [suggestions, setSuggestions] = useState([])

    const handleOnChange = (e, { method }) => {
      if (method === 'enter') {
        e.preventDefault()
      } else {
        props.onChange(e)
      }
    }

    const onSuggestionSelected = (_e, { suggestion }) => {
      addTag({
        name: suggestion.name,
        id: suggestion.id,
        usersCount: suggestion.usersCount,
      })
    }

    const onSuggestionsFetchRequested = ({ value }) =>
      setSuggestions(getSuggestions(value))

    const getSuggestions = (value) => {
      const inputValue = value.trim().toLowerCase()
      const inputLength = inputValue.length

      return inputLength === 0
        ? []
        : workspaceGroups.filter(
            (g) => g.name.toLowerCase().slice(0, inputLength) === inputValue
          )
    }

    return (
      <Autosuggest
        getSuggestionValue={(suggestion) => suggestion.name}
        inputProps={{ ...props, onChange: handleOnChange }}
        onSuggestionsClearRequested={() => {}}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        ref={ref}
        renderSuggestion={GroupSuggestion}
        suggestions={suggestions}
        theme={autosuggest}
      />
    )
  }
)

GroupAutosuggest.displayName = 'GroupAutosuggest'

const AddGroup = ({ elementId, workspaceGroups, workspaceId }) => {
  const { _ } = useLingui()
  const [groups, setGroups] = useState({ tags: [] })
  const [mutate, { loading }] = useMutation(ADD_AUTHORIZATION_TO_GROUPS, {
    refetchQueries: ['GetElementAuthorizationForGroups'],
  })

  const handleTagsChange = (tags) => {
    let validTags = []

    validTags = tags
      .map((tag) => {
        if (typeof tag === 'string') {
          const found = workspaceGroups.find((g) => g.name === tag)
          return found
            ? { name: found.name, id: found.id, usersCount: found.usersCount }
            : null
        } else if (typeof tag === 'object' && tag.name && tag.id) {
          return tag
        }
        return null
      })
      .filter(Boolean)

    // Remove duplicates based on id.
    validTags = Array.from(
      new Map(validTags.map((tag) => [tag.id, tag])).values()
    )

    const tagsToSet = validTags.length > 0 ? validTags : []

    setGroups({ tags: tagsToSet })
  }

  const renderInput = ({ addTag, ...props }) => (
    <GroupAutosuggest
      addTag={addTag}
      {...props}
      workspaceGroups={workspaceGroups}
    />
  )

  const renderTag = ({ key, tag, ...props }) => (
    <GroupTag {...props} index={key} key={key} tag={tag} />
  )

  const isValid = groups.tags.length > 0

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isValid) return

    const groupIds = groups.tags.map((tag) => tag.id)

    try {
      await mutate({
        variables: {
          elementId: elementId,
          groupsIds: groupIds,
        },
      })
    } catch (error) {
      console.error('Error adding groups:', error)
    }

    setGroups({ tags: [] })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <TagsInput
        addKeys={[9, 13, 32, 59, 108, 188, 222]}
        className={styles.tagsInput}
        inputProps={{
          placeholder: _(msg`Invita o aggiungi gruppi...`),
        }}
        onChange={handleTagsChange}
        onlyUnique
        renderTag={renderTag}
        renderInput={renderInput}
        tagDisplayProperty="name"
        value={groups.tags}
      />
      <Button
        disabled={!isValid || loading}
        processing={loading}
        size="XS"
        type="submit"
      >
        <Trans>Condividi</Trans>
      </Button>
    </form>
  )
}

export default AddGroup
